import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import {
  faCreditCard,
  faDownToLine,
  faWallet,
} from '@fortawesome/pro-solid-svg-icons';
import { subYears } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Outlet, generatePath } from 'react-router';

import { Cryptocurrency } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { CryptoIcon } from '@sorare/core/src/atoms/icons/CryptoIcon';
import { Shards } from '@sorare/core/src/atoms/icons/Shards';
import { TabContainer } from '@sorare/core/src/atoms/layout/TabContainer';
import {
  MY_SORARE_TRANSACTIONS_CASH,
  MY_SORARE_TRANSACTIONS_CREDITCARD,
  MY_SORARE_TRANSACTIONS_ESSENCE,
  MY_SORARE_TRANSACTIONS_ETH,
  MY_SORARE_TRANSACTIONS_SOL,
  MY_SORARE_TRANSACTIONS_VIP,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { MyPage } from '../MyPage';
import { AccountDialog } from './AccountDialog';
import type {
  GenerateAccountingExtractMutation,
  GenerateAccountingExtractMutationVariables,
} from './__generated__/index.graphql';
import { TRANSACTION_HISTORY_CONTENT_QUERY } from './query';
import { MySorareSubTabsLayout } from '../MySorareSubTabsLayout';

const GENERATE_ACCOUNTING_EXTRACT_MUTATION = gql`
  mutation GenerateAccountingExtractMutation(
    $input: generateAccountingExtractInput!
  ) {
    generateAccountingExtract(input: $input) {
      errors {
        code
        message
        path
      }
    }
  }
` as TypedDocumentNode<
  GenerateAccountingExtractMutation,
  GenerateAccountingExtractMutationVariables
>;

const messages = defineMessages({
  FIAT: {
    id: 'TransactionTabs.fiat',
    defaultMessage: 'Cash Wallet',
  },
  ETH: {
    id: 'PaymentBox.eth',
    defaultMessage: 'ETH Wallet',
  },
  CREDIT_CARD: {
    id: 'TransactionTabs.creditCard',
    defaultMessage: 'Credit card',
  },
  VIP: {
    id: 'TransactionTabs.vip',
    defaultMessage: 'VIP',
  },
  ESSENCE: {
    id: 'TransactionTabs.essence',
    defaultMessage: 'Essence',
  },
  SOL: {
    id: 'TransactionTabs.sol',
    defaultMessage: 'SOL Wallet',
  },
});

export const TransactionsHistory = () => {
  const { showNotification } = useSnackNotificationContext();
  const [accountDialogOpened, setAccountDialogOpened] = useState(false);
  const [startDate, setStartDate] = useState<Date>(subYears(new Date(), 1));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [generate, { loading }] = useMutation(
    GENERATE_ACCOUNTING_EXTRACT_MUTATION
  );

  const generateAccountExtract = () => {
    generate({
      variables: {
        input: {
          includeFiatEntries: true,
          includeCreditCardPayments: true,
          includeLostBids: true,
          includeCardRewards: true,
          startDate,
          endDate,
        },
      },
    }).then(({ data }) => {
      if (!data?.generateAccountingExtract?.errors.length) {
        showNotification('accountingExtractSent');
      }
    });
  };

  const {
    currentUser,
    walletPreferences: { showFiatWallet, showCryptoWallet },
  } = useCurrentUserContext();
  const hasSolana = currentUser?.wallet?.solanaAddress;

  const { data } = useQuery(TRANSACTION_HISTORY_CONTENT_QUERY);

  if (!data?.currentUser) return null;

  const { highScarcityStatus } = data.currentUser;
  const showHighScarcityStatus =
    highScarcityStatus?.current?.status ||
    highScarcityStatus?.previous?.status ||
    (highScarcityStatus?.spendLeftToNextStatus?.eurCents &&
      highScarcityStatus.spendLeftToNextStatus.eurCents < 7500);

  const subTabs = [
    ...(showCryptoWallet && hasSolana
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_SOL),
            label: messages.SOL,
            icon: <CryptoIcon cryptocurrency={Cryptocurrency.LAMPORT} />,
          },
        ]
      : []),
    ...(showCryptoWallet
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_ETH),
            label: messages.ETH,
            icon: <CryptoIcon cryptocurrency={Cryptocurrency.WEI} />,
          },
        ]
      : []),
    ...(showFiatWallet
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_CASH),
            label: messages.FIAT,
            icon: <FontAwesomeIcon icon={faWallet} />,
          },
        ]
      : []),
    {
      to: generatePath(MY_SORARE_TRANSACTIONS_CREDITCARD),
      label: messages.CREDIT_CARD,
      icon: <FontAwesomeIcon icon={faCreditCard} />,
    },
    ...(showHighScarcityStatus
      ? [
          {
            to: generatePath(MY_SORARE_TRANSACTIONS_VIP),
            label: messages.VIP,
          },
        ]
      : []),
    {
      to: generatePath(MY_SORARE_TRANSACTIONS_ESSENCE),
      label: messages.ESSENCE,
      icon: <Shards size={2} />,
    },
  ];

  return (
    <MySorareSubTabsLayout tabs={subTabs}>
      <MyPage
        extraCta={
          <LoadingButton
            color="tertiary"
            size="small"
            startIcon={<FontAwesomeIcon icon={faDownToLine} />}
            onClick={() => setAccountDialogOpened(true)}
            loading={loading}
          >
            <FormattedMessage
              id="MySorare.TransactionsHistory.downloadCSV2"
              defaultMessage="CSV"
            />
            <AccountDialog
              accountDialogOpened={accountDialogOpened}
              setAccountDialogOpened={setAccountDialogOpened}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              generateAccountExtract={generateAccountExtract}
              loading={loading}
            />
          </LoadingButton>
        }
      >
        <TabContainer>
          <Outlet />
        </TabContainer>
      </MyPage>
    </MySorareSubTabsLayout>
  );
};
